// Domain · YesNoChip
//
// Tone- and label-coded renderer for boolean fields rendered as a chip
// (is_own_company, is_pending_review, etc.). The defaults match the
// most common case — `true` → green "Tak", `false` → grey "Nie" —
// but tone + label are overridable when the domain rule differs.

import React from "react";
import { Pill, type PillSize, type PillTone } from "@ds/display/Pill";

// ─── Inputs ───────────────────────────────────────────────────────────────

export interface YesNoChipInput {
  /** Boolean value. `null` / `undefined` renders an em-dash. */
  value: boolean | null | undefined;
  /** Pill size. Default `"sm"` (table cells). */
  size?: PillSize;
  /** Tone for `true`. Default `"green"`. */
  yesTone?: PillTone;
  /** Tone for `false`. Default `"grey"`. */
  noTone?: PillTone;
  /** Label for `true`. Default `"Tak"`. */
  yesLabel?: string;
  /** Label for `false`. Default `"Nie"`. */
  noLabel?: string;
  /** `data-testid` on the root. Defaults to `"yes-no-chip"`. */
  testId?: string;
}

export type YesNoChipProps = YesNoChipInput;

// ─── Component ────────────────────────────────────────────────────────────

export function YesNoChip({
  value,
  size = "sm",
  yesTone = "green",
  noTone = "grey",
  yesLabel = "Tak",
  noLabel = "Nie",
  testId = "yes-no-chip",
}: YesNoChipProps) {
  if (value == null) {
    return <span data-testid={testId}>—</span>;
  }
  return (
    <Pill tone={value ? yesTone : noTone} size={size} testId={testId}>
      {value ? yesLabel : noLabel}
    </Pill>
  );
}
